import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useEffect, useState, type ReactNode } from "react";
import taskService from "@/services/task.service";
import { format, isValid } from "date-fns";
import { baseURL } from "@/lib/axios";
import { 
  FolderOpen, 
  Building2, 
  Calendar, 
  Users, 
  Target,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  Info,
  User,
  BarChart3,
} from "lucide-react";
interface ViewProjectDialogProps {
  project: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const apiBaseWithoutPrefix = baseURL ? baseURL.replace(/\/api\/v1\/?$/, '') : '';

const buildImageUrlIfNeeded = (path?: string | null) => {
  if (!path) return null;
  if (path.startsWith('http')) {
    return path;
  }
  if (!apiBaseWithoutPrefix) {
    return path;
  }
  const normalizedBase = apiBaseWithoutPrefix.replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
  return `${normalizedBase}/${normalizedPath}`;
};

const getInitials = (firstName?: string, lastName?: string, email?: string) => {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  if (firstName) {
    return firstName.slice(0, 2).toUpperCase();
  }
  if (lastName) {
    return lastName.slice(0, 2).toUpperCase();
  }
  if (email) {
    return email.slice(0, 2).toUpperCase();
  }
  return 'NA';
};

export function ViewProjectDialog({
  project,
  open,
  onOpenChange,
}: ViewProjectDialogProps) {

  const [taskStats, setTaskStats] = useState({ total: 0, completed: 0, inProgress: 0, blocked: 0 });

  // Load tasks and compute stats when dialog opens
  useEffect(() => {
    if (!open) return;
    const projectId = project?._id || project?.id;
    if (!projectId) return;

    let cancelled = false;
    const load = async () => {
      try {
        const tasks = await taskService.getTasksByProject(projectId);
        if (!Array.isArray(tasks)) return;
        const total = tasks.length;
        const norm = (s: any) => (typeof s === 'string' ? s.toUpperCase() : '');
        const completed = tasks.filter(t => ['COMPLETE','COMPLETED','DONE'].includes(norm((t as any).status))).length;
        const inProgress = tasks.filter(t => ['IN_PROGRESS'].includes(norm((t as any).status))).length;
        const blocked = tasks.filter(t => ['ON_HOLD','BLOCKED'].includes(norm((t as any).status))).length;
        if (!cancelled) setTaskStats({ total, completed, inProgress, blocked });
      } catch {
        if (!cancelled) setTaskStats({ total: 0, completed: 0, inProgress: 0, blocked: 0 });
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, project]);

  // Extract employee data from populated fields
  const getProjectManager = () => {
    if (project?.projectManager && typeof project.projectManager === 'object') {
      return {
        name: `${project.projectManager.firstName} ${project.projectManager.lastName}`,
        firstName: project.projectManager.firstName,
        lastName: project.projectManager.lastName,
        email: project.projectManager.email,
        profilePicture: project.projectManager.profilePicture,
        initials: getInitials(project.projectManager.firstName, project.projectManager.lastName, project.projectManager.email)
      };
    }
    return null;
  };

  const getTeamMembersList = () => {
    if (project?.team && Array.isArray(project.team) && project.team.length > 0) {
      return project.team.map((member: any) => ({
        name: `${member.firstName} ${member.lastName}`,
        firstName: member.firstName,
        lastName: member.lastName,
        email: member.email,
        employeeId: member.employeeId,
        profilePicture: member.profilePicture,
        initials: getInitials(member.firstName, member.lastName, member.email)
      }));
    }
    return [];
  };

  const projectManager = getProjectManager();
  const teamMembers = getTeamMembersList();

  const formatDate = (date: any) => {
    if (date && isValid(new Date(date))) {
      return format(new Date(date), "PPP");
    }
    return "Not specified";
  };

  const formatCurrency = (amount: any) => {
    if (amount && !isNaN(amount)) {
      return `₹${Number(amount).toLocaleString("en-IN")}`;
    }
    return "Not specified";
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
      case 'IN_PROGRESS':
        return "bg-blue-50 text-blue-700";
      case 'PLANNING':
        return "bg-yellow-50 text-yellow-700";
      case 'COMPLETED':
        return "bg-green-50 text-green-700";
      case 'ON_HOLD':
        return "bg-red-50 text-red-700";
      default:
        return "bg-gray-50 text-gray-700";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return "bg-red-50 text-red-700";
      case 'MEDIUM':
        return "bg-yellow-50 text-yellow-700";
      case 'LOW':
        return "bg-green-50 text-green-700";
      default:
        return "bg-gray-50 text-gray-700";
    }
  };

  const renderInfoItem = (
    icon: ReactNode,
    label: string,
    value: string | ReactNode,
    iconColor: string = "text-blue-600"
  ) => (
    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0`}>
          <div className={iconColor}>{icon}</div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
          <div className="font-semibold text-gray-900 text-sm leading-tight">
            {typeof value === "string" && (value === "Not provided" || value === "Not specified") ? (
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                {value}
              </span>
            ) : (
              value
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSectionHeader = (
    icon: ReactNode,
    title: string,
    iconBgColor: string,
    iconColor: string
  ) => (
    <div className={`flex items-center gap-3 mb-6`}>
      <div className={`w-10 h-10 ${iconBgColor} rounded-xl flex items-center justify-center`}>
        <div className={iconColor}>{icon}</div>
      </div>
      <h2 className="text-xl font-bold text-gray-900">{title}</h2>
    </div>
  );

  if (!project) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1000px] h-[95vh] flex flex-col p-0 rounded-xl overflow-hidden">
        <DialogHeader className="px-4 pb-0">
          <DialogTitle className="sr-only">
            Project Details - {project.projectName || project.name || "Project"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            View complete project information including details, team, timeline, and progress.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            {/* Project Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100 shadow-sm">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                  {(project.projectName || project.name || "P")[0]}m
                </div>
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-gray-900 mb-1">
                    {project.projectName || project.name || "Not provided"}
                  </h1>
                  <p className="text-gray-600 text-base font-medium mb-2">
                    Client: {project.client || "Not provided"}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-2">
                      <Badge className={getStatusColor(project.status)}>
                        {project.status}
                      </Badge>
                    </span>
                    <span className="flex items-center gap-2">
                      <Badge className={getPriorityColor(project.priority)}>
                        {project.priority} Priority
                      </Badge>
                    </span>
                    <span className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Created: {formatDate(project.createdDate)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Project Information */}
              <div className="bg-blue-50 rounded-xl p-6 shadow-sm border border-blue-100">
                {renderSectionHeader(
                  <FolderOpen className="h-5 w-5" />,
                  "Project Information",
                  "bg-blue-100",
                  "text-blue-600"
                )}
                
                <div className="grid grid-cols-1 gap-4">
                  {renderInfoItem(
                    <Building2 className="h-4 w-4" />,
                    "Client",
                    project.client || "Not provided"
                  )}
                  {renderInfoItem(
                    <Info className="h-4 w-4" />,
                    "Description",
                    project.projectDescription || project.description || "Not provided"
                  )}
                  {renderInfoItem(
                    <DollarSign className="h-4 w-4" />,
                    "Budget",
                    formatCurrency(project.budget)
                  )}
                  {renderInfoItem(
                    <BarChart3 className="h-4 w-4" />,
                    "Progress",
                    <div className="flex items-center gap-2">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${project.progress || 0}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium">{project.progress || 0}%</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Timeline Information */}
              <div className="bg-green-50 rounded-xl p-6 shadow-sm border border-green-100">
                {renderSectionHeader(
                  <Calendar className="h-5 w-5" />,
                  "Timeline",
                  "bg-green-100",
                  "text-green-600"
                )}
                
                <div className="grid grid-cols-1 gap-4">
                  {renderInfoItem(
                    <Calendar className="h-4 w-4" />,
                    "Start Date",
                    formatDate(project.startDate)
                  )}
                  {renderInfoItem(
                    <Calendar className="h-4 w-4" />,
                    "End Date",
                    formatDate(project.endDate)
                  )}
                  {renderInfoItem(
                    <Clock className="h-4 w-4" />,
                    "Duration",
                    (() => {
                      if (project.startDate && project.endDate) {
                        const start = new Date(project.startDate);
                        const end = new Date(project.endDate);
                        const diffTime = Math.abs(end.getTime() - start.getTime());
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        return `${diffDays} days`;
                      }
                      return "Not specified";
                    })()
                  )}
                </div>
              </div>

              {/* Team Information */}
              <div className="bg-purple-50 rounded-xl p-6 shadow-sm border border-purple-100">
                {renderSectionHeader(
                  <Users className="h-5 w-5" />,
                  "Team",
                  "bg-purple-100",
                  "text-purple-600"
                )}
                
                <div className="grid grid-cols-1 gap-4">
                  {renderInfoItem(
                    <User className="h-4 w-4" />,
                    "Project Manager",
                    projectManager ? (
                      <div className="flex items-center gap-3">
                        <div className="relative w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center overflow-hidden text-xs font-semibold text-purple-600">
                          {buildImageUrlIfNeeded(projectManager.profilePicture) ? (
                            <img
                              src={buildImageUrlIfNeeded(projectManager.profilePicture)!}
                              alt={projectManager.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                e.currentTarget.nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                          ) : null}
                          <span className={`${buildImageUrlIfNeeded(projectManager.profilePicture) ? 'hidden' : ''}`}>
                            {projectManager.initials}
                          </span>
                        </div>
                        <span className="text-sm font-medium">{projectManager.name}</span>
                      </div>
                    ) : (
                      "Not assigned"
                    )
                  )}
                  {renderInfoItem(
                    <Users className="h-4 w-4" />,
                    "Team Members",
                    teamMembers.length > 0 ? (
                      <div className="space-y-2">
                        {teamMembers.map((member: any, index: number) => {
                          const imageUrl = buildImageUrlIfNeeded(member.profilePicture);
                          return (
                            <div key={index} className="flex items-center gap-3">
                              <div className="relative w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center overflow-hidden text-xs font-semibold text-purple-600">
                                {imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt={member.name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                    }}
                                  />
                                ) : null}
                                <span className={`${imageUrl ? 'hidden' : ''}`}>
                                  {member.initials}
                                </span>
                              </div>
                              <span className="text-sm">{member.name}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      "No team members assigned"
                    )
                  )}
                </div>
              </div>

              {/* Task Information */}
              <div className="bg-orange-50 rounded-xl p-6 shadow-sm border border-orange-100">
                {renderSectionHeader(
                  <Target className="h-5 w-5" />,
                  "Tasks",
                  "bg-orange-100",
                  "text-orange-600"
                )}
                
                <div className="grid grid-cols-1 gap-4">
                  {renderInfoItem(
                    <Target className="h-4 w-4" />,
                    "Total Tasks",
                    taskStats.total
                  )}
                  {renderInfoItem(
                    <CheckCircle className="h-4 w-4" />,
                    "Completed Tasks",
                    taskStats.completed
                  )}
                  {renderInfoItem(
                    <Clock className="h-4 w-4" />,
                    "In Progress",
                    taskStats.inProgress
                  )}
                  {renderInfoItem(
                    <AlertCircle className="h-4 w-4" />,
                    "Blocked Tasks",
                    taskStats.blocked
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}